import React, { useEffect, useState } from 'react'
import backgroundImage from '../../images/bgd/bg-mobile-new.png'
import { useStoreActions, useStoreState } from '../../model/hooks'
import { useWalletContext } from '../WalletContext'
import { useNavigate, useParams } from 'react-router-dom'
import { BackIcon } from '../../svgIcons/BackIcon'
import { ChartsAppBar } from '../charts/ChartsAppBar'
import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { OutgoingLinkIcon } from '../../svgIcons/OutgoingLink'
import PrimaryButton from '../PrimaryButton'
import { GAtrack } from '../../services/ga'
import { numFormat } from '../../blockchainUtils/math'
import { PrecentChange } from '../charts/PrecentChange'
import BigNumber from 'bignumber.js'
import { formatDollarValue } from '../charts/chartUtils'
import { TokenChartData, TokenOverview, loadTokenChartData, loadTokenOverviewData } from '../charts/chartsApi'
import { VerifiedIcon } from '../../svgIcons/VerifiedIcon'
import { UnkownIcon } from '../../svgIcons/UnknownIcon'
import { FailedIcon } from '../../svgIcons/FailedIcon'
import Popup from '../Popup'
import { XIcon } from '../../svgIcons/XIcon'
import { PoolsTable } from '../charts/PoolsTable'
import Spinner from '../Spinner'
import { removeLeadingZeros } from '../../model/tokens/helper'
import { ChartTypes, ChartsContainer } from './ChartsContainer'
import { Token } from '../../atoms'

export function TokenPage() {
    const { networkName: network } = useWalletContext()
    const loadPrices = useStoreActions((actions) => actions.pricing.loadPrices)
    const { address } = useParams()
    const navigate = useNavigate()
    const tokens = useStoreState((state) => state.tokens.tokens['mainnet-alpha'])
    const [selectedToken, setSelectedToken] = useState(undefined as unknown as Token)
    const [errorLoadingToken, setErrorLoadingToken] = useState('')
    const [selectedChart, setSelectedChart] = useState('Price' as ChartTypes)
    const [overView, setOverview] = useState({} as TokenOverview)
    const [chartData, setChartData] = useState([] as TokenChartData[])
    const [chartAllData, setAllData] = useState([] as TokenChartData[])
    const [isTokenCharacteristicsOpen, setIsTokenCharacteristicsOpen] = useState(false)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        if (selectedToken && selectedToken.address) onLoadTokenOverview()
    }, [selectedToken])

    useEffect(() => {
        if (selectedChart) onLoadTokenChartData()
    }, [selectedChart])

    useEffect(() => {
        if (network === 'mainnet-alpha') GAtrack('route_token_page', 'route_token_page', 'tokens', -1)

        loadTokensPricing()
        const intervalId = setInterval(() => {
            loadTokensPricing()
        }, 180000)

        return () => {
            clearInterval(intervalId)
        }
    }, [])

    function onZoomChart(leftZoom: any, rightZoom: any) {
        if (leftZoom === rightZoom) return
        if (leftZoom === 'dataMin' && rightZoom === 'dataMax') return setChartData(chartAllData)

        if (leftZoom === 'dataMin' || rightZoom === 'dataMax') return

        const start = leftZoom < rightZoom ? leftZoom : rightZoom
        const end = rightZoom > leftZoom ? rightZoom : leftZoom
        const oldData = [...chartData]

        const startPoint = oldData.findIndex((item: TokenChartData) => item.timestamp === start)
        const endPoint = oldData.findIndex((item: TokenChartData) => item.timestamp === end)

        setChartData(oldData.slice(startPoint, endPoint))
    }

    function onSelectChartType(type: ChartTypes) {
        setSelectedChart(type)
    }

    async function onLoadTokenChartData() {
        if (!address) return
        const tokenAddress = removeLeadingZeros(address)

        const data = await loadTokenChartData(selectedChart.toLowerCase() as any, tokenAddress!)

        if (data && data.length > 0) {
            setChartData(data)
            setAllData(data)
        }
    }

    async function onLoadTokenOverview() {
        const tokenAddress = removeLeadingZeros(selectedToken.address)
        const response = await loadTokenOverviewData(tokenAddress)
        if (response) setOverview(response)
    }

    function loadTokensPricing() {
        loadPrices()
    }

    useEffect(() => {
        if (address && tokens) onSelectToken()
    }, [tokens])

    function onSelectToken() {
        if (!tokens || !address) return

        const tokenList = Object.values(tokens)
        const selectedToken = tokenList.find((token) => token.address.toLowerCase() === address.toLowerCase())
        if (selectedToken) setSelectedToken(selectedToken)
        else setErrorLoadingToken('Failed to find token')
    }

    function renderBack() {
        return (
            <div className='w-full mt-[20px] lg:mt-[48px] flex items-center'>
                <div className='cursor-pointer text-[#0AE8F9] flex flex-row' onClick={() => navigate('/charts')}>
                    <BackIcon className='w-5 h-5 mr-2' />
                    <span>Back</span>
                </div>
            </div>
        )
    }

    function renderTokenCharacteristicsModal() {
        if (!isTokenCharacteristicsOpen) return null

        return (
            <Popup contentClassName='w-[528px]' open={isTokenCharacteristicsOpen} onClose={() => setIsTokenCharacteristicsOpen(false)}>
                <div className='flex flex-col w-full items-center'>
                    <XIcon
                        onClick={() => {
                            setIsTokenCharacteristicsOpen(false)
                        }}
                        className='absolute right-[16px] lg:right-[32px] top-[32px] w-[16px] h-[16px] text-[white] z-[10] cursor-pointer'
                    />
                    <div className='w-full mt-[24px] flex flex-col text-white items-center mb-[32px]'>
                        <img height={'60px'} width={'60px'} className='' src={selectedToken.icon} />
                        <span className='text-[18px] font-bold mt-2'>{selectedToken.name}</span>
                        <span className='text-[16px] text-[#8387A5] mt-2'>{selectedToken.symbol}</span>
                    </div>
                    <TokenCharacteristicsContent
                        tokenCharacteristics={{
                            is_code_verified: overView.is_code_verified || 'unknown',
                            is_initial_liquidity_timelock: overView.is_initial_liquidity_timelock || 'unknown',
                            is_none_upgradable: overView.is_none_upgradable || 'unknown',
                            is_not_taxed: overView.is_not_taxed || 'unknown'
                        }}
                    />

                    <PrimaryButton className='w-full mt-[60px]' onClick={() => setIsTokenCharacteristicsOpen(false)}>
                        <span>Ok</span>
                    </PrimaryButton>
                </div>
            </Popup>
        )
    }

    function renderTokenCharsTitle() {
        if (overView && overView.is_bluechip) return null

        return (
            <>
                <div className='h-[20px] w-[1px] bg-[#6D7391] mx-2 lg:flex hidden' />
                <div className='flex items-center cursor-pointer'>
                    <HelptipIcon className='text-[#68F2B0] h-[16px] w-[16px]' />
                    <span className='ml-1 underline text-[12px] text-white font-bold' onClick={() => setIsTokenCharacteristicsOpen(true)}>
                        Token characteristics
                    </span>
                </div>
            </>
        )
    }

    function renderSelectedTokenHeader() {
        if (!selectedToken) return renderLoading()
        return (
            <div className='flex flex-col lg:flex-row lg:items-center mt-[40px] justify-between'>
                <div className='flex lg:items-center lg:flex-row flex-col'>
                    <div className='flex items-center'>
                        <img className='mr-2' src={selectedToken.icon} width={'36px'} height={'36px'} />
                        <span className='text-[24px] font-bold text-white mr-2'>{selectedToken.name}</span>
                        <span className='text-[#6D7391] text-[18px]'>{`(${selectedToken.symbol})`}</span>
                    </div>
                    <div className='flex items-center lg:mt-0 mt-2'>
                        {renderTokenCharsTitle()}
                        <div className='h-[20px] w-[1px] bg-[#6D7391] mx-2 ' />
                        <div className='flex items-center ml-2 cursor-pointer' onClick={() => window.open(`https://voyager.online/token/${selectedToken.address}`)}>
                            <OutgoingLinkIcon className='w-4 h-4 text-white' />
                            <span className='ml-2 text-[12px] font-bold'>View on Voyager</span>
                        </div>
                    </div>
                </div>
                <div className='flex items-center lg:mt-0 mt-2'>
                    <PrimaryButton onClick={() => navigate(`/swap?from=${selectedToken.address}`)}>
                        <span className='text-[16px]'>Swap</span>
                    </PrimaryButton>
                    <PrimaryButton className='ml-3' onClick={() => navigate(`/create-position?token1=${selectedToken.address}`)}>
                        <span className='text-[16px] '>+ Add liquidity</span>
                    </PrimaryButton>
                </div>
            </div>
        )
    }

    function renderTokenPrice() {
        if (!selectedToken || !overView) return renderLoading()

        const change = overView.price_change_24h
        const price = overView.price ?? 0
        return (
            <div className='flex flex-col mt-5'>
                <div className='flex items-center'>
                    <span className='font-bold text-[36px] text-white mr-2'>{`$${numFormat(price.toString())}`}</span>
                    {overView.price_change_24h ? <PrecentChange fontSize={16} value={BigNumber(change)} /> : null}
                </div>
                <span className='text-[#6D7391] text-[12px]'>(Last 24h Change)</span>
            </div>
        )
    }

    function renderTVL() {
        console.log('overView.tvl ', overView.tvl)
        console.log('selectedToken ', selectedToken)
        if (!selectedToken || !overView) return renderLoading()

        const change = overView.tvl_change_24h

        return (
            <div
                className='mb-2 lg:mb-0 lg:items-center w-full lg:w-auto lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <span className='lg:text-[16px] text-[12px]'>TVL</span>
                <div className='flex flex-col mt-2 flex-row items-center'>
                    <span className='lg:text-[32px] mr-2 text-[18px] font-bold'>{`$${formatDollarValue(overView.tvl)}`}</span>
                    {overView.tvl_change_24h ? <PrecentChange fontSize={16} lgFontSize={14} value={BigNumber(change)} /> : null}
                </div>
            </div>
        )
    }

    function renderVolume() {
        if (!selectedToken || overView.volume_24h === undefined || overView.volume_24h === null) return renderLoading()
        const change = overView.volume_change_24h
        return (
            <div
                className='w-full lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <span className='text-[12px] lg:text-[16px]'>Volume 24h</span>
                <div className='flex flex-col mt-2 items-center'>
                    <span className='lg:text-[32px] mr-2 text-[18px] font-bold'>{`$${formatDollarValue(overView.volume_24h)}`}</span>
                    {change ? <PrecentChange fontSize={16} lgFontSize={14} value={BigNumber(change)} /> : null}
                </div>
            </div>
        )
    }

    function renderLoading() {
        return (
            <div className='flex flex-1 mt-3'>
                <Spinner />
            </div>
        )
    }

    function renderVolumeWeek() {
        if (!selectedToken) return renderLoading()
        if (!overView.volume_7d) return null
        const volume = formatDollarValue(overView.volume_7d)
        const percentageChange = BigNumber(overView.volume_change_7d)

        return (
            <div
                className='w-full lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <span className='text-[12px] lg:text-[16px]'>Volume 7d</span>
                <div className='flex flex-col mt-2 items-center'>
                    <span className='lg:text-[32px] mr-2 text-[18px] font-bold'>{`$${volume}`}</span>
                    {overView.volume_change_7d ? <PrecentChange fontSize={16} lgFontSize={14} value={BigNumber(percentageChange)} /> : null}
                </div>
            </div>
        )
    }

    function renderTokenInfo() {
        return (
            <div className='mt-4 flex w-full lg:flex-row lg:w-auto lg:justify-start lg:mb-6 justify-between flex-col '>
                {renderTVL()}
                {renderVolume()}
                {renderVolumeWeek()}
            </div>
        )
    }

    return (
        <div
            className='relative min-h-[100%] w-screen flex flex-col pb-[60px]'
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}>
            <div className='container flex w-full flex-col mx-auto items-center'>
                <ChartsAppBar />
            </div>
            <div className='container flex w-full flex-col mx-auto p-4 lg:p-0'>
                {renderBack()}
                {renderSelectedTokenHeader()}
                {renderTokenPrice()}
                {renderTokenInfo()}
                <ChartsContainer
                    isZoomEnabled={selectedChart === 'Price' || selectedChart === 'TVL'}
                    onZoom={(left, right) => {
                        onZoomChart(left, right)
                    }}
                    chartData={chartData}
                    selectedChart={selectedChart}
                    setSelectedChart={onSelectChartType}
                />
                <div className='flex w-full mt-[60px]'>
                    <div className='w-full'></div>
                </div>
                <div className='my-4' />
                <PoolsTable tokenAddress={selectedToken ? selectedToken.address : ''} />
            </div>

            {renderTokenCharacteristicsModal()}
        </div>
    )
}

export type TokenCharacteristics = {
    is_code_verified: 'verified' | 'unknown' | 'failed'
    is_none_upgradable: 'verified' | 'unknown' | 'failed'
    is_initial_liquidity_timelock: 'verified' | 'unknown' | 'failed'
    is_not_taxed: 'verified' | 'unknown' | 'failed'
}

export const TokenCharacteristicsContent = ({ tokenCharacteristics }: { tokenCharacteristics: TokenCharacteristics }) => {
    function renderCharacteristic(title: string, status: 'verified' | 'unknown' | 'failed' | 'no' | 'yes') {
        let StatusIcon = FailedIcon
        if (status === 'verified' || status === 'yes') StatusIcon = VerifiedIcon
        else if (status === 'unknown') StatusIcon = UnkownIcon
        else if (status === 'failed' || status === 'no') StatusIcon = FailedIcon

        return (
            <div className='text-[16px] text-white flex items-center mt-2'>
                <StatusIcon className='h-[24px] w-[24px]' />
                <span className='ml-2'>{title}</span>
            </div>
        )
    }

    return (
        <div className='bg-[#0C1647] rounded-[16px] py-4 px-5 w-full'>
            <div className='w-full text-center mb-[16px]'>
                <span className='text-white font-bold text-[16px] text-center w-full '>
                    This token is not a known token. Always conduct your own research and assess risks before proceeding.
                </span>
            </div>
            {renderCharacteristic('Code is not verified', tokenCharacteristics.is_code_verified)}
            {renderCharacteristic('Contract is not upgradable', tokenCharacteristics.is_none_upgradable)}
            {renderCharacteristic('Initial liquidity not locked', tokenCharacteristics.is_initial_liquidity_timelock)}
            {renderCharacteristic('No taxes for buy and sell', tokenCharacteristics.is_not_taxed)}
        </div>
    )
}
